import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SingleSelectFilter as DsSingleSelectFilter,
  type SingleSelectFilterOption
} from '@brightlocal/ui-components/single-select-filter';

import { useFilterTriggerSize } from '../hooks';

interface Props<Value extends string> {
  /** Filter name, e.g. "Time period"; titles the mobile drawer and starts the trigger's accessible name. */
  label: string;
  /** Options in the order they are shown. */
  options: readonly SingleSelectFilterOption<Value>[];
  /** Picked value. */
  value: Value;
  /** The card's default for this filter; the drawer's "Clear filters" sets it back. */
  defaultValue: Value;
  /** Called with the newly picked value. */
  onChange: (value: Value) => void;
  /** Test-selector prefix for the trigger, the list or drawer and their rows. */
  dataHook: string;
  /** Greys the trigger out so the filter can't be opened. */
  isDisabled?: boolean;
  /** The trigger button, e.g. to move focus to it. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Filter that picks one option, e.g. Time period or Interval: a select on desktop, a bottom drawer
 * with radio buttons below the `md` breakpoint. Controlled: the card owns the value, so resetting it
 * from outside (the card's own "Clear filters") needs no extra prop.
 */
export const SingleSelectFilter = <Value extends string>({
  ref,
  label,
  options,
  value,
  defaultValue,
  onChange,
  dataHook,
  isDisabled
}: Props<Value>) => {
  const { t } = useTranslation();
  const triggerSize = useFilterTriggerSize();

  return (
    <DsSingleSelectFilter
      ref={ref}
      clearLabel={t('reviewFilters.clearFilters')}
      dataHook={dataHook}
      defaultValue={defaultValue}
      disabled={isDisabled}
      doneLabel={t('reviewFilters.done')}
      drawerDescription={t('reviewFilters.singleSelectDrawerDescription')}
      label={label}
      options={options}
      presentation="auto"
      size={triggerSize}
      value={value}
      onValueChange={onChange}
    />
  );
};
